import type { Hit } from '@algolia/client-search';
import { useState } from 'react';

import { apiClientFetchSynonymSearchApiKey } from '#/api/apiFetchSynonymSearchApiKey';
import { AlgoliaSearchSelect } from '#/components/AlgoliaSearchSelect/AlgoliaSearchSelect';
import { Button } from '#/components/Button/Button';
import { Field } from '#/components/Form/Field/Field';
import { FieldLabel } from '#/components/Form/Field/FieldLabel';
import { useFormContext } from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { LocationField } from '#/components/Form/LocationField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { Icon } from '#/components/Icon/Icon';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import type { OptionType } from '#/components/Select/Select.types';
import { Text } from '#/components/Text/Text';
import { useAppConfig } from '#/config/AppConfigContext';
import { useAlgoliaConfigByIndex } from '#/hooks/useAlgoliaConfigByIndex';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { TitleDescriptionModal } from '#/modules/salary/modals/TitleDescription/TitleDescriptionModal';
import type { SalariesReportFormData } from '#/modules/salary/types/SalariesReportFormData';
import { getSearchAlgoliaConfigWithApiKey } from '#/modules/search/algolia/searchAlgoliaConfig';
import { ViewportBreakpoint } from '#/theme/theme';
import type { SerializedAddressComponent } from '#/utils/googleAddresses';
import { serializeAddress } from '#/utils/googleAddresses';
import { RichError } from '#/utils/RichError';
import { trackError } from '#/utils/trackError';

import {
  ButtonContainer,
  FieldLabelContainer,
  FormFieldContainer,
  GeneralErrorContainer,
  IconContainer,
  SubmitButtonContainer,
  YearsOfExperienceInputContainer,
} from './MainChartSidebarForm.styled';

function OptionLabel(
  option: OptionType<
    Hit<{
      jobTitle: {
        id: string;
        description: string;
        familyId: string;
        level: number;
      };
      name: string;
    }>
  >,
) {
  const {
    value: { name },
  } = option;
  return <Text qaId="autocomplete-suggestion">{name}</Text>;
}

type Props = {
  searchApiKey: string;
  setSearchApiKey: (searchApiKey: string) => void;
};

type MainChartSidebarFormSynonymOption = {
  objectID: string;
  jobTitle: {
    id: string;
    description: string;
    familyId: string;
    level: number;
  };
  name: string;
};

export function MainChartSidebarForm({ searchApiKey, setSearchApiKey }: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const { algoliaConfigByIndex } = useAlgoliaConfigByIndex();

  const [showModal, setShowModal] = useState(false);
  const { clearErrors, updateValue, updateValues, updateValueWith, values } =
    useFormContext<SalariesReportFormData>();

  const breakpoint = useViewportBreakpoint();
  const isMobile =
    breakpoint === ViewportBreakpoint.sm ||
    breakpoint === ViewportBreakpoint.xs;

  const selectAddress = ({
    city,
    geoType,
    stateCode,
    latitude,
    longitude,
  }: SerializedAddressComponent) => {
    updateValueWith(
      ['geoType', 'stateCode', 'city', 'latitude', 'longitude'],
      ({
        city: formCity,
        geoType: formGeoType,
        stateCode: formStateCode,
        latitude: formLatitude,
        longitude: formLongitude,
      }: SalariesReportFormData) => ({
        city: city ?? formCity,
        geoType: geoType || formGeoType,
        stateCode: stateCode || formStateCode,
        latitude: latitude ?? formLatitude,
        longitude: longitude ?? formLongitude,
        cityStateUnified: city && stateCode && `${city}, ${stateCode}`,
      }),
    );
  };

  const handlePlaceDetails = (place: google.maps.GeocoderResult) => {
    const serializedAddress = serializeAddress(i18n, place);
    selectAddress({ ...serializedAddress, geoType: place.types[0] });

    // https://developers.google.com/maps/documentation/javascript/reference/places-widget#Autocomplete.getPlace  -- failure case
    if (!serializedAddress.city) {
      trackError(
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        new RichError('Failed to fetch API result', JSON.stringify(place)),
      );
    }
  };

  return (
    <>
      <Field<SalariesReportFormData>
        name="jobTitle"
        label={`${i18n('Job Title')}*`}
        customErrorMatch={(errors) => errors.selectedSynonymId}
        renderInput={({ qaId, getValue }) => {
          return (
            <AlgoliaSearchSelect<MainChartSidebarFormSynonymOption>
              instanceId="main-chart-sidebar-synonym"
              qaId={qaId}
              placeholder={i18n('Program Manager')}
              value={
                getValue('selectedSynonymId')
                  ? {
                      value: {
                        objectID: getValue('selectedSynonymId'),
                        name: getValue('jobTitle'),
                        jobTitle: {
                          id: getValue('jobTitleId'),
                          description: getValue('jobDescription'),
                          familyId: getValue('selectedSynonymFamilyId'),
                          level: getValue('selectedSynonymLevel'),
                        },
                      } as MainChartSidebarFormSynonymOption,
                      label: getValue('jobTitle') as string,
                    }
                  : null
              }
              onChange={(option) => {
                if (!option) return;

                updateValues({
                  jobDescription: option.value.jobTitle.description,
                  jobTitle: option.value.name,
                  jobTitleId: option.value.jobTitle.id,
                  selectedSynonymId: option.value.objectID,
                  selectedSynonymFamilyId: option.value.jobTitle.familyId,
                  selectedSynonymLevel: option.value.jobTitle.level,
                });
                clearErrors(['selectedSynonymId']);
              }}
              searchArgs={{
                config: getSearchAlgoliaConfigWithApiKey({
                  apiKey: searchApiKey,
                  appConfig,
                }),
                ...algoliaConfigByIndex.SALARY_REPORT_SURVEY_TITLES,
                onApiKeyTimeout: () =>
                  apiClientFetchSynonymSearchApiKey().then((newApiKey) => {
                    setSearchApiKey(newApiKey);
                    return newApiKey;
                  }),
              }}
              formatOptionLabel={OptionLabel}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? i18n('No options') : null
              }
            />
          );
        }}
      />

      {values.selectedSynonymId && (
        <>
          <ButtonContainer>
            <Button
              type="button"
              onClick={() => setShowModal(true)}
              noPadding
              qaId="read-title-description-button"
              variant="tertiary-white"
              size="tiny"
            >
              {i18n('Read title description')}
            </Button>
          </ButtonContainer>

          {showModal && (
            <TitleDescriptionModal
              onClose={() => setShowModal(false)}
              jobDescription={values.jobDescription}
              jobTitle={values.jobTitle}
            />
          )}
        </>
      )}

      <FormFieldContainer>
        <FieldLabelContainer>
          <FieldLabel>{i18n('Location')}</FieldLabel>
        </FieldLabelContainer>
      </FormFieldContainer>

      <LocationField
        name="cityStateUnified"
        addressTypes={['locality']}
        placeholder={i18n('e.g. Hawkins, IN')}
        includedRegionCodes={['US']}
        handlePlaceDetails={handlePlaceDetails}
        locationText={values.cityStateUnified}
        onClear={() =>
          updateValueWith(
            ['geoType', 'city', 'stateCode', 'latitude', 'longitude'],
            () => ({
              geoType: null,
              city: null,
              stateCode: null,
              latitude: null,
              longitude: null,
            }),
          )
        }
        customErrorMatch={(errors) => errors.stateCode}
      />

      <FormFieldContainer>
        <FieldLabelContainer>
          <FieldLabel>{i18n('Years of Relevant Experience')}</FieldLabel>
        </FieldLabelContainer>

        <LegacyTooltip green lightColor>
          {i18n(
            `Includes experience in the role listed above and any time spent in similar/transferable roles. For example: if you have 2 years of experience in fundraising and 1 in social work—and the role above is in fundraising—you'd have 2 years of relevant experience.`,
          )}
        </LegacyTooltip>
      </FormFieldContainer>

      <YearsOfExperienceInputContainer>
        <InputField
          autoComplete="off"
          name="yearsOfExperience"
          type="text"
          inputMode="numeric"
          onChange={(e) => {
            const { value } = e.target;

            if (value !== '' && !/^\d+$/.test(value)) return;

            updateValue(
              'yearsOfExperience',
              value === '' ? null : Number(value),
            );
          }}
        />
      </YearsOfExperienceInputContainer>

      <GeneralErrorContainer>
        <GeneralError />
      </GeneralErrorContainer>

      <SubmitButtonContainer>
        <SubmitButton
          size="small"
          fullWidth={isMobile}
          disabled={!searchApiKey}
          variant="secondary-white"
        >
          <Text styleAs="h5">{i18n("Let's Go")}</Text>
        </SubmitButton>
      </SubmitButtonContainer>

      <IconContainer>
        <Icon colorName="white" name="arrow-down" size={24} />
      </IconContainer>
    </>
  );
}
